import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getFinanceInvoiceList } from "../../services/vendor";
import { dateTimeFormatter } from "../../services/helper";
import {
  Button,
  Input,
  Pagination,
  Select,
  Table,
  Card,
  Row,
  Col,
  Tag,
  Space,
} from "antd";
import { getCompanyList, getVendorList } from "../../services/vendorBills";
import { Eye, X, Filter, RotateCcw, Download } from "lucide-react";
import { InvoiceDetailsModal } from "./modal/InvoiceDetailsModal";
import { capitalizeFirstLetter, getStatusColor } from "../../utils";

const { Option } = Select;

const Invoices = () => {
  const [invoicesData, setInvoicesData] = useState([]);
  const [listPayload, setListPayload] = useState({
    limit: 10,
    offset: 0,
    vendor_id: null,
    company_id: null,
    bill_month: null,
  });
  const [totalLengthListData, setTotalLengthListData] = useState(0);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);

  const fetchCompanyListData = async () => {
    try {
      const response = await getCompanyList();
      if (response.status === 200) {
        let companyData = response?.data?.data;
        companyData = companyData?.map((item) => ({
          label: `${item.company_name} - ${item.state}`,
          value: item.id,
        }));
        setCompanyOptions(companyData);
      }
    } catch (error) {
      toast.error("Error fetching imprest data.");
    }
  };

  const fetchVendorListData = async () => {
    try {
      const response = await getVendorList();
      if (response.status === 200) {
        let vendorListData = response?.data?.data;
        vendorListData = vendorListData?.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setVendorOptions(vendorListData);
      }
    } catch (error) {
      toast.error("Error fetching imprest data.");
    }
  };

  useEffect(() => {
    fetchVendorListData();
    fetchCompanyListData();
    getInvoicesList(listPayload);
  }, []);

  const getInvoicesList = async ({
    limit = 10,
    offset = 0,
    vendor_id = null,
    company_id = null,
    bill_month = null,
  }) => {
    const payload = {
      limit,
      offset,
      vendor_id,
      company_id,
      search: bill_month,
    };
    const response = await getFinanceInvoiceList(payload);
    const { data, page_number } = response?.data || {};
    console.log({ data, page_number, response });
    if (response?.status === 200) {
      setInvoicesData(response?.data?.invoices?.data);
      setTotalLengthListData(response?.data?.invoices?.total);
    } else {
      toast.error("Error getting vendor invoices");
    }
  };

  const paginationHandler = (page, limit) => {
    getInvoicesList({
      ...listPayload,
      limit,
      offset: page * limit - limit,
    });
    setListPayload((pre) => ({
      ...pre,
      limit,
      offset: page * limit - limit,
    }));
  };

  const applyFilterHandler = () => {
    getInvoicesList(listPayload);
  };

  const filterButtonDisable =
    listPayload.vendor_id == null &&
    listPayload.company_id == null &&
    listPayload.bill_month == null;

  const [selectedInvoiceNew, setSelectedInvoiceNew] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoiceNew(invoice);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedInvoiceNew(null);
  };

  const applyResetHandler = () => {
    getInvoicesList({
      ...listPayload,
      vendor_id: null,
      company_id: null,
      bill_month: null,
    });
    setListPayload((pre) => ({
      ...pre,
      vendor_id: null,
      company_id: null,
      bill_month: null,
    }));
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 140,
      render: (text) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      width: 120,
      render: (date) => dateTimeFormatter(date, false) || "N/A",
    },
    {
      title: "Bill Month",
      dataIndex: "bill_month",
      key: "bill_month",
      width: 100,
    },
    {
      title: "Vendor Name",
      dataIndex: ["vendor", "name"],
      key: "vendor_name",
      render: (name) => name || "N/A",
      width: 130,
    },
    {
      title: "Company Name",
      dataIndex: ["company", "company_name"],
      key: "company_name",
      render: (name) => name || "N/A",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Requestor Email",
      dataIndex: "requester_email",
      key: "requester_email",
      width: 150,
    },
    {
      title: "Payable Amount",
      dataIndex: "payable_amount",
      key: "payable_amount",
      width: 130,
      render: (amount) => (
        <span className="font-medium">{amount || "N/A"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} className="font-medium">
          {capitalizeFirstLetter(status)}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      fixed: "right",
      render: (_, record) => (
        <Space size="small" className="flex-nowrap">
          <Button
            type="primary"
            ghost
            size="small"
            onClick={() => handleViewInvoice(record)}
            className="flex items-center justify-center min-w-[60px]"
          >
            View
          </Button>
          {record.status === "PENDING" && (
            <Button
              type="primary"
              ghost
              danger
              size="small"
              className="flex items-center justify-center min-w-[70px]"
            >
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const downloadHandler = () => {
    const params = new URLSearchParams();
    if (listPayload.vendor_id) params.append("vendorId", listPayload.vendor_id);
    if (listPayload.company_id)
      params.append("companyId", listPayload.company_id);
    if (listPayload.bill_month) params.append("search", listPayload.bill_month);
    params.append("token", localStorage.token);
    const downloadUrl = `https://stage.techtonicretail.com/finance/api/vendor/invoices/report/download?${params.toString()}`;
    window.open(downloadUrl, "_blank");
  };

  return (
    <>
      <div className="min-h-screen">
        <div
          style={{
            marginBottom: 24,
            color: "#0471a3",
            fontSize: "1.5rem",
            fontWeight: 600,
          }}
        >
          Vendor Invoices
        </div>
        <div className="max-w-7xl mx-auto mt-6">
          {/* Filters Section */}
          <Card className="mb-6 shadow-sm">
            {/* Top Section: Filters + Download Button */}
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={6}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor
                  </label>
                  <Select
                    placeholder="Select Vendor"
                    value={listPayload.vendor_id}
                    onChange={(value) =>
                      setListPayload((pre) => ({
                        ...pre,
                        vendor_id: value ?? null,
                      }))
                    }
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {vendorOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={6}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <Select
                    placeholder="Select Company"
                    value={listPayload.company_id}
                    onChange={(value) =>
                      setListPayload((pre) => ({
                        ...pre,
                        company_id: value ?? null,
                      }))
                    }
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {companyOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={8}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <Input
                    placeholder="Search by Invoice Number, Bill Month"
                    value={listPayload.bill_month}
                    onChange={(e) =>
                      setListPayload((pre) => ({
                        ...pre,
                        bill_month: e.target.value || null,
                      }))
                    }
                  />
                </div>
              </Col>

              <Col xs={24} sm={4} className="flex justify-end items-end">
                <Button icon={<Download size={16} />} onClick={downloadHandler}>
                  Download
                </Button>
              </Col>
            </Row>

            {/* Bottom Section: Reset and Apply Filter */}
            <Row className="mt-4">
              <Col xs={24}>
                <div className="flex justify-end gap-2 flex-wrap sm:flex-nowrap">
                  <Button
                    onClick={applyResetHandler}
                    icon={<RotateCcw size={16} />}
                    style={{
                      backgroundColor: "#0471a3",
                      borderColor: "#0471a3",
                      color: "#fff",
                    }}
                  >
                    Reset Filter
                  </Button>

                  <Button
                    onClick={applyFilterHandler}
                    disabled={filterButtonDisable}
                    icon={<Filter size={16} />}
                    style={{
                      backgroundColor: filterButtonDisable
                        ? "#f0f0f0"
                        : "#0471a3",
                      borderColor: filterButtonDisable ? "#d9d9d9" : "#0471a3",
                      color: filterButtonDisable ? "#a0a0a0" : "#fff",
                    }}
                  >
                    Apply Filter
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Invoice Table */}
          {/* <Card className="shadow-sm"> */}
          <div className="bg-white pb-2">
            <Table
              columns={columns}
              dataSource={invoicesData}
              pagination={false}
              bordered
              scroll={{ x: 1400 }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                marginTop: 16,
                marginBottom: 16,
                marginRight: 10,
                textAlign: "right",
              }}
            >
              <Pagination
                showQuickJumper
                total={totalLengthListData}
                showSizeChanger={true}
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={paginationHandler}
              />
            </div>
          </div>
          {/* </Card> */}
        </div>

        {/* Invoice Details Modal */}
        <InvoiceDetailsModal
          visible={isModalVisible}
          onClose={handleCloseModal}
          invoice={selectedInvoiceNew}
        />
      </div>
    </>
  );
};

export default Invoices;
